// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';
import { Line2 } from 'three/addons/lines/Line2';
import { LineGeometry } from 'three/addons/lines/LineGeometry';

import type { HighlightableObjectName } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import { HighlightableLineGroup } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableLineGroup';
import { labelTheming } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/labelTheming';

export type AngleGeometryOpts = {
  center: THREE.Vector3;
  /** The radius of the arc in world units */
  radius: number;
  /** The start angle in radians */
  startAngle: number;
  /** The end angle in radians */
  endAngle: number;
  /** The rotation of the label */
  rotation: THREE.Quaternion;
};

export class AngleGeometry extends HighlightableLineGroup {
  public readonly arcMidPointLocal: THREE.Vector3;

  constructor(
    highlightableObjectName: HighlightableObjectName,
    { center, radius, startAngle, endAngle, rotation }: AngleGeometryOpts,
  ) {
    super(highlightableObjectName, 'dimensionLine');

    const pointCount = this.getPointCount(startAngle, endAngle, radius);

    // Create a set of points along the arc, then add the baseline.
    const arcPoints = new THREE.ArcCurve(0, 0, radius, startAngle, Math.PI * 2 - endAngle, true)
      .getPoints(pointCount)
      .map((p) => new THREE.Vector3(0, p.y, p.x));

    // Store the arc midpoint before pushing the center point so the index is not
    // skewed toward the arc end by the extra element.
    const arcMidpointIndex = Math.floor(pointCount / 2);
    this.arcMidPointLocal = (arcPoints[arcMidpointIndex] ?? new THREE.Vector3()).clone();

    arcPoints.push(new THREE.Vector3());

    const lineGeometry = new LineGeometry();
    lineGeometry.setFromPoints(arcPoints);

    const arcLine = new Line2(lineGeometry, this.lineMaterial);
    arcLine.renderOrder = labelTheming.renderOrder.labelOnBend;
    this.add(arcLine);

    this.setRotationFromQuaternion(rotation);
    // Put the whole group at the center point.
    this.position.copy(center);
  }

  /**
   * Helper method to calculate the number of points along the arc that will be used to draw the arc.
   */
  private getPointCount(startAngle: number, endAngle: number, radius: number) {
    // Targets roughly one segment per 3 degrees on a circle with radius 1,
    // scaling linearly with the radius and capped at 64 to avoid excessive geometry.
    return THREE.MathUtils.clamp(
      Math.ceil((THREE.MathUtils.radToDeg(Math.abs(endAngle - startAngle)) / 3) * radius),
      2,
      64,
    );
  }
}
